import * as Sentry from '@sentry/nextjs';
import { atom } from 'jotai';
import { exhaustMap } from 'rxjs';

import SuspensionModal from 'src/components/SuspensionModal';
import { removeTokenAtom, userDataAtom } from 'src/stores/auth/atoms';
import { showModalAtom } from 'src/stores/modal/atoms';
import { ModalType } from 'src/types/Modal';
import { ErrorResponse, PunishedException } from 'src/utils/api';
import { atomWithPipes } from 'src/utils/atom';
import { isAxiosError } from 'src/utils/error';
import { getAtomWithStorage, reviveDateFromStorage } from 'src/utils/localStorageUtils';

import { getFeatureSettingsAPI, getUpdatedUserInfoAPI, setFeatureSettingsAPI } from './api';
import { FeatureSettings, SetFeatureSettingsParam } from './types';

export const lastGetLegalUpdatesDateAtom = getAtomWithStorage<Date | null>(
  'checkLegalUpdateDate',
  null,
  { reviver: reviveDateFromStorage }
);

export const featureSettingsAtom = atom<FeatureSettings['featureSettings'] | null>(null);

export const getFeatureSettingsAtom = atom(null, (_get, set) => {
  return getFeatureSettingsAPI().then(({ data }) => {
    set(featureSettingsAtom, data.result.featureSettings);
  });
});

export const setFeatureSettingsAtom = atom(
  null,
  async (get, set, params: SetFeatureSettingsParam['featureSettings']) => {
    await setFeatureSettingsAPI({ featureSettings: params });
    const featureSettings = get(featureSettingsAtom);

    const newSettings = featureSettings ? [...featureSettings] : null;
    if (newSettings) {
      for (const param of params) {
        for (const setting of newSettings) {
          if (setting.name === param.type) {
            setting.value = param.value;
            break;
          }
        }
      }
      set(featureSettingsAtom, newSettings);
    }
  }
);

const DEVICE_ID_KEY = 'deviceId';
export const deviceIdAtom = getAtomWithStorage<string | void>(DEVICE_ID_KEY, undefined);

export const lastUserInfoUpdateDateAtom = getAtomWithStorage<Date | null>(
  'lastGetSegmentTime',
  null,
  {
    reviver: reviveDateFromStorage,
  }
);

export const getUpdatedUserInfoAtom = atomWithPipes(
  null,
  async (get, set) => {
    const userData = get(userDataAtom);
    if (!userData) return;

    try {
      const {
        data: { result },
      } = await getUpdatedUserInfoAPI();
      set(lastUserInfoUpdateDateAtom, new Date());
      set(
        userDataAtom,
        (prevUserData) =>
          prevUserData && {
            ...prevUserData,
            remoteConfig: result.remoteConfig,
            featureOptions: result.featureOptions,
            userProfile: {
              ...prevUserData.userProfile,
              location: result.location,
            },
          }
      );
    } catch (err) {
      if (!isAxiosError<ErrorResponse<PunishedException>>(err)) return;
      const code = err?.response?.data.error?.code;
      const errorBody = err?.response?.data.error?.data;
      if (!code) return;

      switch (code) {
        case 'PUNISHED':
          if (errorBody) {
            set(showModalAtom, {
              key: ModalType.LOGIN_SUSPENSION,
              component: () =>
                SuspensionModal({
                  punishment: errorBody,
                }),
            });
          } else {
            Sentry.captureMessage('PUNISHED error body is empty', {
              extra: {
                error: err?.response?.data.error,
              },
            });
          }

          set(removeTokenAtom);
          break;
        default:
          throw err;
      }
    }
  },
  [exhaustMap((x) => x())]
);
